import { useState, useEffect } from "react";
import { Copy, Merge, Search, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { useMasters } from "./MastersContext";
import { toast } from "sonner";

const SECTIONS = [
  { key: "customers", type: "customer", label: "Customers" },
  { key: "people", type: "person", label: "People" },
  { key: "brokers", type: "broker", label: "Brokers" },
  { key: "sub_transporters", type: "sub_transporter", label: "Sub-transporters" },
  { key: "drivers", type: "driver", label: "Drivers" },
  { key: "vehicles", type: "vehicle", label: "Vehicles" },
  { key: "locations", type: "location", label: "Locations / Addresses" },
];

function subtitle(e) {
  const bits = [];
  if (e.vehicle_type) bits.push(e.vehicle_type);
  if (e.gst_number || e.gst) bits.push(e.gst_number || e.gst);
  if (e.pan) bits.push(e.pan);
  if (e.phone) bits.push(e.phone);
  if (e.role) bits.push(e.role);
  if (e.email) bits.push(e.email);
  // address only as a subtitle when it isn't already the row's main label
  if (e.address && (e.name || e.vehicle_number)) bits.push(e.address);
  if (e.uses) bits.push(`${e.uses} use${e.uses > 1 ? "s" : ""}`);
  return bits.join(" · ");
}

function matchReason(group) {
  const allSame = (vals) => vals.length === group.length && new Set(vals).size === 1;
  const gsts = group.map((g) => (g.gst_number || g.gst || "").trim().toLowerCase()).filter(Boolean);
  if (allSame(gsts)) return "Same GSTIN";
  const pans = group.map((g) => (g.pan || "").trim().toLowerCase()).filter(Boolean);
  if (allSame(pans)) return "Same PAN";
  const phones = group.map((g) => (g.phone || "").replace(/\D/g, "")).filter((p) => p.length >= 7);
  if (allSame(phones)) return "Same phone";
  return null;
}

function Group({ type, group, onMerge }) {
  const [keepId, setKeepId] = useState(group[0].id);
  const [busy, setBusy] = useState(false);
  const mergeIds = group.filter((g) => g.id !== keepId).map((g) => g.id);
  const reason = matchReason(group);

  async function doMerge() {
    setBusy(true);
    try { await onMerge(type, keepId, mergeIds); toast.success("Merged"); }
    catch { toast.error("Merge failed"); }
    finally { setBusy(false); }
  }

  return (
    <div className="border border-[#E5E3DB] rounded p-3 mb-3" data-testid={`dupe-group-${type}-${group[0].id}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className="text-[11px] uppercase tracking-wide text-[#68665E]">{group.length} possible duplicates — pick the one to keep</div>
        {reason && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[#E7EEF9] text-[#2B5FB3]" data-testid={`dupe-reason-${group[0].id}`}>{reason}</span>}
      </div>
      <div className="space-y-1.5">
        {group.map((e) => {
          const sub = subtitle(e);
          return (
            <label key={e.id} className={`flex items-start gap-2 p-2 rounded cursor-pointer text-sm ${keepId === e.id ? "bg-[#E7F2EC]" : "hover:bg-[#F5F4F0]"}`} data-testid={`dupe-option-${e.id}`}>
              <input type="radio" name={`keep-${group[0].id}`} checked={keepId === e.id} onChange={() => setKeepId(e.id)} className="mt-1" />
              <span className="flex-1">
                <span className="font-medium text-[#1A1A18]">{e.name || e.vehicle_number || e.address || "—"}</span>
                {sub && <span className="text-[#68665E]"> · {sub}</span>}
              </span>
              {keepId === e.id && <CheckCircle2 className="w-4 h-4 text-[#2F6F4E] shrink-0" />}
            </label>
          );
        })}
      </div>
      <button onClick={doMerge} disabled={busy || mergeIds.length === 0} className="btn-primary text-xs mt-2" data-testid={`dupe-merge-${group[0].id}`}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Merge className="w-3.5 h-3.5" />} Merge {mergeIds.length} into selected
      </button>
    </div>
  );
}

export function DuplicatesSection() {
  const { scanDuplicates, mergeMaster, mergeSafeDupes, autoMergeAll, getAutoMergeSetting, setAutoMergeSetting } = useMasters();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(false);
  const [autoOn, setAutoOn] = useState(true);

  useEffect(() => { getAutoMergeSetting().then(setAutoOn).catch(() => {}); }, []); // eslint-disable-line

  async function onMergeAll() {
    setMerging(true);
    try {
      const r = await autoMergeAll();
      toast.success(r.merged_records ? `Auto-merged ${r.merged_groups} group${r.merged_groups > 1 ? "s" : ""} (${r.merged_records} record${r.merged_records > 1 ? "s" : ""})` : "No duplicates to merge — directories are clean");
      await scan();
    } catch { toast.error("Auto-merge failed"); }
    finally { setMerging(false); }
  }
  async function toggleAuto() {
    const next = !autoOn;
    setAutoOn(next);
    try {
      await setAutoMergeSetting(next);
      toast.success(next ? "Auto-merge is ON — duplicates merge automatically" : "Auto-merge turned off");
      if (next) await onMergeAll();
    } catch { setAutoOn(!next); toast.error("Could not save setting"); }
  }

  async function scan() {
    setLoading(true);
    try { setData(await scanDuplicates()); }
    catch { toast.error("Scan failed"); }
    finally { setLoading(false); }
  }
  // Auto-scan once on mount so the user gets a duplicate-address nudge without clicking.
  useEffect(() => { scan(); }, []); // eslint-disable-line
  async function onMerge(type, keep, ids) {
    await mergeMaster(type, keep, ids);
    await scan();
  }
  async function onMergeSafe() {
    setMerging(true);
    try {
      const r = await mergeSafeDupes();
      toast.success(r.merged_groups ? `Merged ${r.merged_groups} GSTIN duplicate group${r.merged_groups > 1 ? "s" : ""} (${r.merged_records} record${r.merged_records > 1 ? "s" : ""})` : "No Same-GSTIN duplicates to merge");
      await scan();
    } catch { toast.error("Auto-merge failed"); }
    finally { setMerging(false); }
  }

  const total = data ? SECTIONS.reduce((n, s) => n + ((data[s.key] || []).length), 0) : 0;
  const addrGroups = data ? (data.locations || []) : [];
  const addrDupes = addrGroups.reduce((n, g) => n + g.length, 0);
  const gstinGroups = data
    ? ["customers", "brokers", "sub_transporters"].reduce((n, k) => n + (data[k] || []).filter((g) => matchReason(g) === "Same GSTIN").length, 0)
    : 0;

  return (
    <section className="lg:col-span-2" id="dupe-section">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2"><Copy className="w-4 h-4 text-[#C84B31]" /><div className="section-title mb-0">Review Duplicates</div><span className="text-[11px] text-[#68665E]">(scans every master directory)</span></div>
        <button onClick={scan} className="btn-secondary text-xs" disabled={loading} data-testid="dupe-scan-button">{loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />} {data ? "Rescan" : "Scan for duplicates"}</button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 mb-3 px-3 py-2 rounded-md bg-[#E7F2EC] border border-[#B9DcC7]" data-testid="auto-merge-bar">
        <label className="flex items-center gap-2 cursor-pointer text-sm text-[#2F6F4E]" data-testid="auto-merge-toggle-label">
          <input type="checkbox" checked={autoOn} onChange={toggleAuto} className="w-4 h-4 accent-[#2F6F4E]" data-testid="auto-merge-toggle" />
          <span><span className="font-semibold">Auto-merge duplicates automatically</span> — same-name companies, people & addresses are merged for you (kept most-used)</span>
        </label>
        <button onClick={onMergeAll} disabled={merging} className="btn-primary text-xs" data-testid="merge-all-now-button">
          {merging ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Merge className="w-3.5 h-3.5" />} Merge all now
        </button>
      </div>
      {gstinGroups > 0 && (
        <button
          onClick={onMergeSafe}
          disabled={merging}
          className="w-full flex items-center gap-2 text-left mb-3 px-3 py-2 rounded-md bg-[#E7EEF9] border border-[#Bcd0ee] text-[#2B5FB3] text-sm hover:bg-[#DCE7F6] transition-colors disabled:opacity-60"
          data-testid="merge-safe-button">
          {merging ? <Loader2 className="w-4 h-4 animate-spin shrink-0" /> : <Merge className="w-4 h-4 shrink-0" />}
          <span><span className="font-semibold">Merge all Same-GSTIN duplicates</span> — {gstinGroups} group{gstinGroups > 1 ? "s" : ""} share an identical GSTIN · one-tap safe merge</span>
        </button>
      )}
      {addrGroups.length > 0 && (
        <button
          onClick={() => document.getElementById("dupe-location-heading")?.scrollIntoView({ behavior: "smooth", block: "center" })}
          className="w-full flex items-center gap-2 text-left mb-3 px-3 py-2 rounded-md bg-[#FBEAD1] border border-[#E8C98A] text-[#8A5A0B] text-sm hover:bg-[#F8E0BE] transition-colors"
          data-testid="dupe-address-nudge">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span><span className="font-semibold">{addrDupes} possible duplicate address{addrDupes > 1 ? "es" : ""}</span> in {addrGroups.length} group{addrGroups.length > 1 ? "s" : ""} — tap to review & merge</span>
        </button>
      )}
      <div className="table-wrap p-4">
        {!data ? (
          <div className="text-sm text-[#68665E]" data-testid="dupe-idle">Click “Scan for duplicates” to find near-duplicates across Customers, People, Brokers, Sub-transporters, Drivers, Vehicles & Locations/Addresses (matched by name, GST/PAN, phone, number or address).</div>
        ) : total === 0 ? (
          <div className="text-sm text-[#2F6F4E] flex items-center gap-2" data-testid="dupe-none"><CheckCircle2 className="w-4 h-4" /> No duplicates found — your directories are clean.</div>
        ) : (
          <div>
            {SECTIONS.map((s) => {
              const groups = data[s.key] || [];
              if (groups.length === 0) return null;
              return (
                <div key={s.key} className="mb-4">
                  <div className="text-xs font-semibold text-[#68665E] mb-2" data-testid={`dupe-${s.type}-heading`}>{s.label} ({groups.length} group{groups.length > 1 ? "s" : ""})</div>
                  {groups.map((g) => <Group key={g[0].id} type={s.type} group={g} onMerge={onMerge} />)}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
